// SurveyJS bridge. The quiz JSON is built at import time, before the language
// is known, so localizable properties (title, description, choice text, html,
// placeholder, ...) get SurveyJS's own multi-locale form, filled from the
// i18next resources:   loc("quiz.goals.title") -> { default: "<en>", es: "<es>" }
// survey.locale then picks the language (see applySurveyLanguage), and a
// later language switch re-renders without rebuilding the model.
// SurveyJS placeholders like {name} stay inside the strings; i18next does not
// interpolate them here because the raw resource text is used.
import i18n, { I18N_STRICT, reportMissingKey } from "./index";
import { resources } from "./resources";

export type LocText = { default: string; es?: string };

function lookup(lng: keyof typeof resources, key: string): string | undefined {
  const value = key
    .split(".")
    .reduce<unknown>((node, part) => (node && typeof node === "object" ? (node as Record<string, unknown>)[part] : undefined), resources[lng].translation);
  return typeof value === "string" ? value : undefined;
}

export function loc(key: string): LocText {
  const en = lookup("en", key);
  if (en === undefined) {
    // A wrong key is a code bug in any build.
    throw new Error(`i18n: no English text for ${key}`);
  }
  const es = lookup("es", key);
  if (es === undefined) {
    // Prod falls back to English; strict test builds fail when Spanish is shown.
    if (I18N_STRICT) return { default: en, es: `⟦missing es ${key}⟧` };
    return { default: en };
  }
  return { default: en, es };
}

/** loc() with the same markup wrapped around every language: locWrap(key, (s) => `<b>${s}</b>`). */
export function locWrap(key: string, wrap: (text: string) => string): LocText {
  const text = loc(key);
  return text.es === undefined ? { default: wrap(text.default) } : { default: wrap(text.default), es: wrap(text.es) };
}

/** Resolve a loc() value (or a plain string / element) for the active language. */
export function pickLoc<T>(value: LocText | T): string | T {
  if (value && typeof value === "object" && "default" in (value as LocText)) {
    const text = value as LocText;
    if (i18n.language === "es") {
      if (text.es === undefined || text.es.startsWith("⟦missing es")) reportMissingKey("es", text.default);
      return text.es ?? text.default;
    }
    return text.default;
  }
  return value as T;
}

/** i18n.t for SurveyJS functions (calculated values) that return display text. */
export function tSurvey(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, options ?? {});
}
